"use client";

import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  ArrowRight,
  Loader2,
  Clock,
  Presentation,
  FileX2,
  Lock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { WHATSAPP_CONSULTOR_GENERAL } from "@/lib/constants";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------
const STORE_COUNT_OPTIONS = ["1", "2-3", "4-6", "7-10", "10+"] as const;

const leadLandingSchema = z.object({
  nomeCompleto: z.string().min(2, "Nome obrigatório"),
  nomeRede: z.string().min(1, "Nome da rede / empresa obrigatório"),
  whatsapp: z.string().min(14, "Informe um WhatsApp válido"),
  quantidadeLojas: z.enum(STORE_COUNT_OPTIONS, {
    error: "Selecione uma opção",
  }),
});

type LeadLandingValues = z.infer<typeof leadLandingSchema>;

// ---------------------------------------------------------------------------
// Phone mask — (00) 00000-0000
// ---------------------------------------------------------------------------
const maskPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 11);
  if (digits.length === 0) return "";
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
};

// ---------------------------------------------------------------------------
// Next steps data
// ---------------------------------------------------------------------------
const NEXT_STEPS = [
  {
    icon: Clock,
    text: "Você recebe contato em até 1 dia útil",
  },
  {
    icon: Presentation,
    text: "Demo personalizada para a realidade da sua rede",
  },
  {
    icon: FileX2,
    text: "Sem compromisso, sem contrato",
  },
] as const;

// ---------------------------------------------------------------------------
// Field wrapper
// ---------------------------------------------------------------------------
const FieldWrapper = ({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <Label htmlFor={htmlFor} className="text-sm font-medium text-foreground">
      {label}
    </Label>
    {children}
    {error && (
      <span role="alert" className="text-xs text-destructive">
        {error}
      </span>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Success state
// ---------------------------------------------------------------------------
const SuccessMessage = () => (
  <motion.div
    key="success"
    initial={{ opacity: 0, scale: 0.96, y: 12 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.96 }}
    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    className="flex flex-col items-center justify-center gap-5 py-14 text-center"
    role="status"
    aria-live="polite"
  >
    <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
      <CheckCircle2 className="w-8 h-8 text-emerald-400" aria-hidden />
    </div>
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-bold text-foreground">Recebemos seu contato!</h3>
      <p className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
        Nossa equipe entrará em contato em até 1 dia útil para agendar sua demo personalizada.
      </p>
    </div>
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Lock className="w-3 h-3" aria-hidden />
      Seus dados estão seguros e não serão compartilhados.
    </span>
  </motion.div>
);

// ---------------------------------------------------------------------------
// LeadFormSection
// ---------------------------------------------------------------------------
export default function LeadFormSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<LeadLandingValues>({
    resolver: zodResolver(leadLandingSchema),
  });

  const onSubmit = async (data: LeadLandingValues) => {
    setLoading(true);

    // Simula chamada de API — substitua pelo endpoint real quando disponível
    await new Promise((resolve) => setTimeout(resolve, 1200));

    // Abre WhatsApp com os dados do lead como mensagem estruturada
    const msg = [
      `Olá! Vim pela landing page e gostaria de agendar uma demo do NoWey.`,
      ``,
      `*Nome:* ${data.nomeCompleto}`,
      `*Rede:* ${data.nomeRede}`,
      `*WhatsApp:* ${data.whatsapp}`,
      `*Lojas:* ${data.quantidadeLojas}`,
    ].join("\n");

    window.open(
      `https://wa.me/${WHATSAPP_CONSULTOR_GENERAL}?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener,noreferrer"
    );

    setLoading(false);
    setSubmitted(true);
  };

  return (
    <section
      id="contato"
      className="relative z-10 py-24 overflow-hidden"
      aria-labelledby="lead-form-headline"
    >
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute inset-0 bg-primary/5" />
        <div className="absolute top-0 left-0 right-0 h-px bg-border/50" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border/50" />
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] h-[70%] bg-primary/8 rounded-full blur-[140px]" />
      </div>

      <div className="max-w-5xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <h2
            id="lead-form-headline"
            className="text-3xl md:text-5xl font-black tracking-tight text-foreground mb-4"
          >
            Vamos mostrar o NoWey{" "}
            <span className="text-primary">para sua rede</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-md mx-auto">
            Preencha o formulário e nossa equipe entra em contato em até 1 dia útil.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start"
        >
          {/* ── Left: Form (3/5) ── */}
          <div className="lg:col-span-3 rounded-2xl border border-border/50 bg-card/60 backdrop-blur-sm p-7">
            <AnimatePresence mode="wait">
              {submitted ? (
                <SuccessMessage key="success" />
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25 }}
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="flex flex-col gap-5"
                  aria-label="Formulário de solicitação de demo"
                >
                  {/* Nome completo */}
                  <FieldWrapper
                    label="Nome completo"
                    htmlFor="nomeCompleto"
                    error={errors.nomeCompleto?.message}
                  >
                    <Input
                      id="nomeCompleto"
                      type="text"
                      autoComplete="name"
                      placeholder="João da Silva"
                      aria-invalid={!!errors.nomeCompleto}
                      {...register("nomeCompleto")}
                    />
                  </FieldWrapper>

                  {/* Nome da rede */}
                  <FieldWrapper
                    label="Nome da rede / empresa"
                    htmlFor="nomeRede"
                    error={errors.nomeRede?.message}
                  >
                    <Input
                      id="nomeRede"
                      type="text"
                      autoComplete="organization"
                      placeholder="Grupo Alfa Veículos"
                      aria-invalid={!!errors.nomeRede}
                      {...register("nomeRede")}
                    />
                  </FieldWrapper>

                  {/* WhatsApp */}
                  <FieldWrapper
                    label="WhatsApp"
                    htmlFor="whatsapp"
                    error={errors.whatsapp?.message}
                  >
                    <Controller
                      name="whatsapp"
                      control={control}
                      render={({ field }) => (
                        <Input
                          id="whatsapp"
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel"
                          placeholder="(11) 99999-9999"
                          aria-invalid={!!errors.whatsapp}
                          value={field.value ?? ""}
                          onChange={(e) => {
                            field.onChange(maskPhone(e.target.value));
                          }}
                        />
                      )}
                    />
                  </FieldWrapper>

                  {/* Quantas lojas */}
                  <FieldWrapper
                    label="Quantas lojas tem?"
                    htmlFor="quantidadeLojas"
                    error={errors.quantidadeLojas?.message}
                  >
                    <Controller
                      name="quantidadeLojas"
                      control={control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <SelectTrigger
                            id="quantidadeLojas"
                            aria-invalid={!!errors.quantidadeLojas}
                            className="rounded-2xl"
                          >
                            <SelectValue placeholder="Selecione..." />
                          </SelectTrigger>
                          <SelectContent>
                            {STORE_COUNT_OPTIONS.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt === "10+" ? "10 ou mais" : opt}{" "}
                                {opt === "1" ? "loja" : "lojas"}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </FieldWrapper>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="mt-1 h-12 w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-sm shadow-[0_0_20px_var(--primary)] hover:shadow-[0_0_30px_var(--primary)] transition-all duration-300 gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                    aria-label="Quero uma demo gratuita"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                        Enviando…
                      </>
                    ) : (
                      <>
                        Quero uma demo gratuita
                        <ArrowRight className="w-4 h-4" aria-hidden />
                      </>
                    )}
                  </Button>

                  <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
                    <Lock className="w-3 h-3" aria-hidden />
                    Seus dados não serão compartilhados com terceiros.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>

          {/* ── Right: Next steps (2/5) ── */}
          <div className="lg:col-span-2 flex flex-col gap-8 pt-2">
            <div className="flex flex-col gap-2">
              <h3 className="text-lg font-bold text-foreground">
                O que acontece depois
              </h3>
              <p className="text-sm text-muted-foreground">
                Processo simples, sem enrolação.
              </p>
            </div>

            <ul className="flex flex-col gap-5" aria-label="Próximos passos">
              {NEXT_STEPS.map(({ icon: Icon, text }, i) => (
                <motion.li
                  key={text}
                  initial={{ opacity: 0, x: 16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{
                    duration: 0.45,
                    delay: 0.15 + i * 0.1,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                  className="flex items-start gap-4"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/12 border border-primary/25 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-primary" aria-hidden />
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed pt-2">
                    {text}
                  </p>
                </motion.li>
              ))}
            </ul>

            {/* Trust badge */}
            <div className="rounded-xl border border-border/50 bg-card/40 p-4 flex items-start gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" aria-hidden />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Mais de <span className="text-foreground font-semibold">12 redes</span> já usam o NoWey para gerenciar seus veículos e times de venda.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
